import { useState, useEffect } from 'react';

/**
 * Hook to detect user's reduced motion preference
 * @returns boolean indicating if user prefers reduced motion
 */
export const useReducedMotion = (): boolean => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') {
      return;
    }

    // Create media query
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    // Set initial value
    setPrefersReducedMotion(mediaQuery.matches);

    // Create event handler
    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    // Add listener
    mediaQuery.addEventListener('change', handleChange);

    // Cleanup
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  return prefersReducedMotion;
};

/**
 * Hook to get animation configuration that respects reduced motion preference
 * @param config - Animation configuration object
 * @returns Modified configuration that respects user preferences
 */
export const useAccessibleAnimation = <T extends Record<string, any>>(
  config: T
): T => {
  const prefersReducedMotion = useReducedMotion();

  if (prefersReducedMotion) {
    return {
      ...config,
      duration: 0,
      delay: 0,
      transition: { duration: 0 },
      animate: config.animate ? { ...config.animate, transition: { duration: 0 } } : undefined,
      initial: config.animate || config.initial,
      whileHover: undefined,
      whileTap: undefined,
      whileInView: config.animate || config.initial,
    };
  }

  return config;
};

/**
 * Utility function to create motion-safe variants
 */
export const createMotionSafeVariants = (
  normalVariants: Record<string, any>,
  reducedVariants?: Record<string, any>
) => {
  const prefersReducedMotion = typeof window !== 'undefined' && 
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (prefersReducedMotion && reducedVariants) {
    return reducedVariants;
  }

  if (prefersReducedMotion) {
    // Create reduced motion versions of normal variants
    const safeVariants: Record<string, any> = {};
    
    Object.keys(normalVariants).forEach(key => {
      const variant = normalVariants[key];
      if (typeof variant === 'object' && variant !== null) {
        safeVariants[key] = {
          ...variant,
          transition: { duration: 0 }
        };
      } else {
        safeVariants[key] = variant;
      }
    });

    return safeVariants;
  }

  return normalVariants;
};